import React from "react";

import { useFormContext } from "react-hook-form/dist/react-hook-form.ie11";

import {
  Box,
  Grid,
  LinearProgress,
  TableCell,
  TableRow,
  TextField,
  Typography,
  colors,
  makeStyles,
} from "@material-ui/core";

import { FranchiseType } from "@hubcapp/types";

import { LearnerCapacityProps } from "./types";
import {
  calculateCapacityProgress,
  getCapacityLabelsByFranchiseType,
} from "../helpers";
import CapacityAvatar from "../CapacityAvatar";

const useStyles = makeStyles(theme => ({
  cell: { padding: theme.spacing(3) },
  flexGrow: {
    flexGrow: 1,
  },
  input: {
    maxWidth: "200px",
  },
  inputLabel: {
    color: colors.blueGrey[900],
  },
  capacityRoot: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    maxHeight: "96px",
  },
  learnerCapacityTitle: {
    fontSize: "12px",
    color: colors.blueGrey[600],
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(-1),
  },
  progress: {
    margin: theme.spacing(0, 4),
    flexGrow: 1,
  },
  progressBar: {
    backgroundColor: "#0D73D9",
  },
}));

const LearnerCapacity: React.FC<LearnerCapacityProps> = ({
  current,
  maximum,
  franchiseType = FranchiseType.Full,
  onLearnerCapacityChange = () => {},
  onLearnerCapacityClick = () => {},
  capLock,
}) => {
  const classes = useStyles();

  const formContext = useFormContext();
  const errors = formContext?.errors;

  const handleLearnerCapacityChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onLearnerCapacityChange(e);
  };

  return (
    <TableRow>
      <TableCell
        classes={{ root: classes.cell }}
        data-test={`learner-capacity-${franchiseType.toLowerCase()}`}
      >
        <Grid container spacing={3} classes={{ root: classes.flexGrow }}>
          <Grid item md={4} sm={5} xs={12}>
            <TextField
              disabled={capLock}
              value={isNaN(maximum) ? "" : maximum}
              onChange={handleLearnerCapacityChange}
              onClick={onLearnerCapacityClick}
              label={getCapacityLabelsByFranchiseType(franchiseType).inputLabel}
              type="number"
              name={`learner-capacity-input-${franchiseType.toLowerCase()}`}
              inputProps={{
                min: "0",
                step: "1",
                ["data-test"]: `learner-capacity-input-${franchiseType.toLowerCase()}`,
              }}
              classes={{ root: classes.input }}
              InputLabelProps={{
                shrink: true,
                classes: { root: classes.inputLabel },
              }}
              variant="outlined"
              error={Boolean(errors?.learnerCapacity?.maximum)}
              helperText={errors?.learnerCapacity?.maximum?.message || ""}
            />
          </Grid>
          <Grid item md={8} sm={7} xs={12} classes={{ root: classes.flexGrow }}>
            <Box className={classes.capacityRoot}>
              <Box flexGrow={1}>
                <Typography
                  component="h4"
                  color="inherit"
                  classes={{ root: classes.learnerCapacityTitle }}
                >
                  {
                    getCapacityLabelsByFranchiseType(franchiseType)
                      .capacityLabel
                  }
                </Typography>

                <Box display="flex" alignItems="center" flexWrap="wrap">
                  <Typography
                    data-test={`learner-capacity-text-${franchiseType.toLowerCase()}`}
                    variant="body1"
                    color="textPrimary"
                  >
                    {current} of {maximum || 0}
                  </Typography>
                  <LinearProgress
                    className={classes.progress}
                    classes={{
                      root: classes.progress,
                      bar: classes.progressBar,
                    }}
                    value={calculateCapacityProgress(maximum, current)}
                    variant="determinate"
                  />
                </Box>
              </Box>
              <CapacityAvatar
                hasCapacity={current < maximum}
                isLocked={capLock}
              />
            </Box>
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
};

export default LearnerCapacity;
